import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  getSeatAddOnQuote,
  purchaseSeatAddOn,
  confirmSeatAddOn,
} from "@/services/subscriptionService";

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Backend errors come back as { error } (fail helper) or { message }.
const errMsg = (err, fallback) =>
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

/**
 * Reusable "buy more employee seats" dialog.
 *
 * Flow: pick N → server-computed quote (prorated at the plan's per-user rate) →
 * pay via a Razorpay ORDER → confirm on the server (which also runs off the
 * order.paid webhook, so this is idempotent) → onPurchased() refreshes the caller.
 *
 * Props:
 *   open, onOpenChange     – controlled visibility
 *   initialSeats           – seats to prefill (e.g. the limit shortfall). Default 1.
 *   onPurchased            – called after seats are successfully applied
 */
export default function BuySeatsDialog({
  open,
  onOpenChange,
  initialSeats = 1,
  onPurchased,
}) {
  const [seats, setSeats] = useState(Math.max(1, initialSeats || 1));
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [paying, setPaying] = useState(false);
  const debounceRef = useRef(null);

  // Reset to the requested shortfall each time the dialog opens.
  useEffect(() => {
    if (open) {
      setSeats(Math.max(1, initialSeats || 1));
      setQuote(null);
      setQuoteError(null);
    }
  }, [open, initialSeats]);

  // Debounced quote whenever the seat count changes while open.
  useEffect(() => {
    if (!open) return;
    if (!seats || seats < 1) {
      setQuote(null);
      return;
    }
    setLoadingQuote(true);
    setQuoteError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const q = await getSeatAddOnQuote(seats);
        setQuote(q);
      } catch (err) {
        setQuote(null);
        setQuoteError(errMsg(err, "Couldn't calculate the price."));
      } finally {
        setLoadingQuote(false);
      }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [seats, open]);

  const changeSeats = (delta) =>
    setSeats((s) => Math.max(1, Math.min(1000, (Number(s) || 1) + delta)));

  const handlePay = useCallback(async () => {
    if (!seats || seats < 1) return;
    setPaying(true);
    try {
      const order = await purchaseSeatAddOn(seats);
      const { razorpay_order_id, amount_paise, amount } = order || {};
      if (!razorpay_order_id) {
        throw new Error("Could not start the payment.");
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        order_id: razorpay_order_id,
        amount: amount_paise,
        currency: "INR",
        name: "Element Tree – Zenith Payroll",
        description: `Add ${seats} employee${seats > 1 ? "s" : ""}`,
        image:
          "https://elementree-website-assets.s3.ap-south-1.amazonaws.com/images/logo/Logo+with+Name+and+Slogan+-+Full+Colour.png",
        handler: async function (rzp) {
          try {
            await confirmSeatAddOn({
              razorpay_order_id,
              razorpay_payment_id: rzp.razorpay_payment_id,
              razorpay_signature: rzp.razorpay_signature,
            });
            toast.success(
              `Added ${seats} employee${seats > 1 ? "s" : ""} to your plan.`
            );
          } catch (err) {
            // Payment went through; the webhook is the backstop if confirm fails.
            console.error("Seat add-on confirm failed:", err);
            toast.info(
              "Payment received. Your new seats will be active in a moment."
            );
          } finally {
            setPaying(false);
            onOpenChange(false);
            onPurchased?.();
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info("Payment cancelled.");
          },
        },
        theme: { color: "#3194A0" },
      };

      new window.Razorpay(options).open();
    } catch (err) {
      console.error("Seat purchase error:", err);
      toast.error(errMsg(err, "Unable to start payment. Please try again."));
      setPaying(false);
    }
  }, [seats, onOpenChange, onPurchased]);

  const canPay = !!quote && !loadingQuote && !quoteError && !paying;

  return (
    <Dialog open={open} onOpenChange={(v) => !paying && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-xl font-semibold">
            Add employees
          </DialogTitle>
          <DialogDescription>
            You've used all your paid seats. Add more at your plan's per-user
            rate, charged only for the days left in this billing cycle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="seat-count">How many employees to add?</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => changeSeats(-1)}
                disabled={paying || seats <= 1}
                aria-label="Decrease"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="seat-count"
                type="number"
                min={1}
                max={1000}
                value={seats}
                onWheel={(e) => e.target.blur()}
                onChange={(e) =>
                  setSeats(
                    Math.max(1, Math.min(1000, parseInt(e.target.value, 10) || 1))
                  )
                }
                className="w-24 text-center"
                disabled={paying}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => changeSeats(1)}
                disabled={paying || seats >= 1000}
                aria-label="Increase"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            {loadingQuote ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
              </div>
            ) : quoteError ? (
              <div className="text-destructive">{quoteError}</div>
            ) : quote ? (
              <div className="space-y-1.5">
                {/*
                  MONTHS, not days.

                  This line used to read "× {remaining_days}/{total_cycle_days}
                  days", but the charge is and always was computed in whole
                  months (computeEmployeeProration). So the formula shown did
                  not equal the total beside it — at 171/365 days it described
                  ₹1,874 while charging ₹2,000 — and any customer who checked
                  the arithmetic found it wrong.
                */}
                <Row
                  label={`${quote.extra_employees} × ${inr(
                    quote.price_per_user
                  )}/user × ${quote.remaining_months}/${quote.total_cycle_months} month${
                    quote.total_cycle_months === 1 ? "" : "s"
                  }`}
                  value={inr(quote.base_amount)}
                />
                <Row label="GST (18%)" value={inr(quote.gst_amount)} />
                <div className="my-1 border-t" />
                <Row
                  label={<span className="font-medium">Pay now</span>}
                  value={
                    <span className="font-semibold">
                      {inr(quote.payable_now)}
                    </span>
                  }
                />
                {/*
                  The month in progress is billed in FULL, so buying just
                  before it ends pays a whole month for a day or two of it.
                  That is the pricing, but springing it on someone is not on —
                  warn while there is still time to wait, and say exactly what
                  waiting saves.
                */}
                {quote.days_to_next_boundary != null &&
                  quote.days_to_next_boundary <= 5 &&
                  quote.remaining_months > 1 && (
                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                      <span className="font-semibold">
                        This billing month ends in {quote.days_to_next_boundary}{" "}
                        {quote.days_to_next_boundary === 1 ? "day" : "days"}
                        {quote.next_month_boundary
                          ? ` (${new Date(quote.next_month_boundary).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                            })})`
                          : ""}
                        .
                      </span>{" "}
                      Part-months are charged in full, so you are paying for all
                      of it. Buying on or after that date costs{" "}
                      {inr(Math.round(quote.payable_now / quote.remaining_months))} less.
                    </div>
                  )}

                <p className="pt-1 text-xs text-muted-foreground">
                  Charged for the {quote.remaining_months} month
                  {quote.remaining_months === 1 ? "" : "s"} left in this billing
                  cycle, current month included
                  {quote.next_billing_date
                    ? ` (until ${new Date(
                        quote.next_billing_date
                      ).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })})`
                    : ""}
                  .
                  {quote.seats_after != null &&
                    ` From the next renewal you'll be billed for ${quote.seats_after} employees at the full rate.`}
                </p>
              </div>
            ) : (
              <div className="text-muted-foreground">
                Choose how many employees to add.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={paying}
          >
            Cancel
          </Button>
          <Button onClick={handlePay} disabled={!canPay}>
            {paying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…
              </>
            ) : quote ? (
              `Pay ${inr(quote.payable_now)}`
            ) : (
              "Pay"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
